/**
 * Cached version of WDApi that stores fetch results in memory
 * Uses the URI as the cache key to avoid redundant API calls
 * @class WDApiCache
 */

import WDApi from './wd-api';

export default class WDApiCache extends WDApi
{
  // Static cache map shared across all instances
  static #cache = new Map();

  /**
   * Constructs a new WDApiCache instance.
   * @param {Object|string} baseCfg - Configuration object or URI string for the API request.
   * @param {Object} values - Optional values to pass to the parent constructor.
   */
  constructor(baseCfg, values)
  {
    super(baseCfg, values);
  }

  /**
   * Gets the cache key for the current request
   * @returns {string} The cache key based on the request URI and parameters
   */
  #getCacheKey()
  {
    const uri = this.getReqURI();
    const params = this.toObject();
    return JSON.stringify({ uri, params });
  }

  /**
   * Override the req() method to implement caching
   * @returns {Promise} Promise that resolves with cached or fresh data
   */
  req()
  {
    const cacheKey = this.#getCacheKey();
    
    //console.log('Cache Key',cacheKey);

    // Check if we have cached data for this request
    if (WDApiCache.#cache.has(cacheKey)) {
      // Return cached data wrapped in a Promise
      //console.log('Using Cache',this.getReqURI());
      return Promise.resolve(WDApiCache.#cache.get(cacheKey));
    }

    // No cache hit, make the actual request
    return super.req().then(data => {
      // Cache the response data
      //console.log('Setting Cache',this.getReqURI());
      WDApiCache.#cache.set(cacheKey, data);
      return data;
    });
  }

  /**
   * Override the post() method to implement caching
   * @param {string} uri - Optional URI to append to the request
   * @param {Object} data - Optional data to send with the POST request
   * @returns {Promise} Promise that resolves with cached or fresh data
   */
  post(uri, data)
  {
    // Create cache key including POST data and URI suffix
    const baseUri = this.getReqURI();
    const params = this.toObject();
    const postData = {
      baseUri: baseUri,
      uri: uri,
      data: data,
      params: params
    };
    const cacheKey = 'POST::' + JSON.stringify(postData);
    
    // Check if we have cached data for this POST request
    if (WDApiCache.#cache.has(cacheKey)) {
      // Return cached data wrapped in a Promise
      return Promise.resolve(WDApiCache.#cache.get(cacheKey));
    }

    // No cache hit, make the actual POST request
    return super.post(uri, data).then(response => {
      // Cache the response data
      WDApiCache.#cache.set(cacheKey, response);
      return response;
    });
  }

  /**
   * Clear all cached data
   * @static
   */
  static clearCache()
  {
    WDApiCache.#cache.clear();
  }

  /**
   * Clear cached data for a specific URI pattern
   * @static
   * @param {string} uriPattern - URI pattern to match for cache clearing
   */
  static clearCacheFor(uriPattern)
  {
    for (const [key] of WDApiCache.#cache) {
      if (key.includes(uriPattern)) {
        WDApiCache.#cache.delete(key);
      }
    }
  }

  /**
   * Get cache statistics
   * @static
   * @returns {Object} Object containing cache size and keys
   */
  static getCacheStats()
  {
    return {
      size: WDApiCache.#cache.size,
      keys: Array.from(WDApiCache.#cache.keys())
    };
  }

  /**
   * Cleanup method to abort pending requests and optionally clear cache entry
   * @param {boolean} clearCache - Whether to clear this instance's cache entry
   */
  destroy(clearCache = false) {
    if (clearCache) {
      const cacheKey = this.#getCacheKey();
      WDApiCache.#cache.delete(cacheKey);
    }
    
    // Call parent destroy to clean up WDApi resources
    if (super.destroy) {
      super.destroy();
    }
    
    return this;
  }
}
